// Scout Hub — komunitní scouting: veřejné focusy, tipy komunity, žebříčky.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Binoculars, Trophy, ArrowLeft, PaperPlaneTilt, Trash, CaretDown, CaretUp, ThumbsUp, Robot } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { scoutFocuses, scoutFocusDetail, scoutSubmit, scoutRetract, scoutLeaderboard, searchPlayers, FOCUS_TYPES } from "../lib/lab-api";

const TYPE_LABEL = Object.fromEntries(FOCUS_TYPES.map((t) => [t.key, t.label]));

const REC_BADGE = {
    best_fit: { label: "Best Fit", cls: "border-fa-mint/60 text-fa-mint bg-fa-mint/10" },
    safe_pick: { label: "Safe Pick", cls: "border-fa-sky/60 text-fa-sky bg-fa-sky/10" },
    upside_pick: { label: "Upside Pick", cls: "border-fa-yellow/60 text-fa-yellow bg-fa-yellow/10" },
    differential: { label: "Differential", cls: "border-fa-violet/80 text-fa-cream bg-fa-violet/20" },
    watch: { label: "Watch", cls: "border-white/30 text-white/60 bg-white/5" },
};

function PlayerPicker({ onPick }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return; }
        const h = setTimeout(() => searchPlayers(q, 6).then((d) => setResults(d.results || [])).catch(() => {}), 250);
        return () => clearTimeout(h);
    }, [q]);
    return (
        <div className="relative">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledej hráče k tipnutí…"
                   data-testid="scout-player-search"
                   className="w-full bg-black border border-white/15 font-mono text-[11px] px-3 py-2 text-white" />
            {results.length > 0 && (
                <div className="absolute z-20 left-0 right-0 top-full mt-1 border border-white/15 bg-[#0d0d0d] max-h-56 overflow-y-auto">
                    {results.map((p) => (
                        <button key={p.fpl_id} type="button"
                                onClick={() => { onPick(p); setQ(""); setResults([]); }}
                                data-testid={`scout-pick-${p.fpl_id}`}
                                className="w-full text-left px-3 py-2 hover:bg-white/[0.05] flex items-center justify-between">
                            <span className="font-display font-bold text-sm">{p.name}</span>
                            <span className="font-mono text-[9px] text-white/45">{p.team} · {p.position} · £{p.price}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function FocusDetail({ fid, onBack, user }) {
    const [data, setData] = useState(null);
    const [picked, setPicked] = useState(null);
    const [reason, setReason] = useState("");
    const [openReasons, setOpenReasons] = useState({});

    const load = useCallback(() => {
        scoutFocusDetail(fid).then(setData).catch(() => toast.error("Načtení focusu selhalo"));
    }, [fid]);
    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!picked) return;
        try {
            const res = await scoutSubmit(fid, { fpl_id: picked.fpl_id, reason: reason.trim() || undefined });
            toast.success(res.xp_awarded ? "Tip odeslán · +1 Knowledge XP" : "Tip odeslán");
            setPicked(null); setReason("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Odeslání selhalo");
        }
    };

    const retract = async (fplId) => {
        try { await scoutRetract(fid, fplId); toast.success("Doporučení staženo"); load(); }
        catch { toast.error("Stažení selhalo"); }
    };

    const vote = async (rec) => {
        if (rec.my_vote) { retract(rec.fpl_id); return; }
        try {
            const res = await scoutSubmit(fid, { fpl_id: rec.fpl_id });
            toast.success(res.xp_awarded ? "Palec nahoru · +1 Knowledge XP" : "Palec nahoru");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Hlasování selhalo");
        }
    };

    if (!data) return <p className="font-mono text-[11px] text-white/40 py-8 text-center">Načítám…</p>;
    const f = data.focus;
    return (
        <div className="space-y-4" data-testid="scout-focus-detail">
            <button type="button" onClick={onBack} data-testid="scout-back"
                    className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1">
                <ArrowLeft size={11} /> Zpět na seznam
            </button>
            <div className="border border-fa-sky/30 bg-fa-sky/5 p-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-fa-sky">{TYPE_LABEL[f.type] || f.type} · od {f.owner_name}</div>
                <h2 className="font-display font-black text-xl uppercase mt-0.5" data-testid="scout-focus-title">{f.title}</h2>
                <div className="font-mono text-[10px] text-white/55 mt-1.5 flex gap-3 flex-wrap">
                    {f.position_filters?.length > 0 && <span>Pozice: {f.position_filters.join("/")}</span>}
                    {f.budget_max != null && <span>Budget: do £{f.budget_max}</span>}
                    <span>GW {f.gw_start}–{f.gw_end}</span>
                    {f.risk_level && <span>Riziko: {f.risk_level}</span>}
                </div>
                {f.requirements && <p className="font-mono text-[10px] text-white/65 mt-2">{f.requirements}</p>}
            </div>

            {/* Seznam AI Recruitera — komunitní palcování */}
            {(data.recruiter_items || []).length > 0 && (
                <div className="space-y-2" data-testid="scout-recruiter-list">
                    <h3 className="font-display font-black uppercase text-sm flex items-center gap-1.5">
                        <Robot size={14} className="text-fa-sky" /> Tipy AI Recruitera — palcuj, co se ti líbí
                    </h3>
                    {data.recruiter_items.map((rec) => {
                        const badge = REC_BADGE[rec.recommendation_type] || REC_BADGE.watch;
                        const limitFull = data.my_submissions >= data.my_limit && !rec.my_vote;
                        return (
                            <div key={rec.fpl_id} className="border border-white/10 bg-[#0d0d0d] p-3" data-testid={`scout-rec-${rec.fpl_id}`}>
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <button type="button" onClick={() => user && !limitFull && vote(rec)}
                                                disabled={!user || limitFull}
                                                data-testid={`scout-vote-${rec.fpl_id}`}
                                                title={!user ? "Přihlas se pro hlasování" : limitFull ? `Vyčerpáno ${data.my_limit}/3 doporučení` : rec.my_vote ? "Vzít palec zpět" : "Palec nahoru (+1 XP)"}
                                                className={`flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[11px] font-bold tabular-nums transition-colors ${
                                                    rec.my_vote ? "border-fa-mint bg-fa-mint text-black"
                                                    : limitFull || !user ? "border-white/15 text-white/35"
                                                    : "border-fa-mint/50 text-fa-mint hover:bg-fa-mint hover:text-black"}`}>
                                            <ThumbsUp size={12} weight={rec.my_vote ? "fill" : "regular"} /> {rec.votes}
                                        </button>
                                        <div className="min-w-0">
                                            <Link to={`/players/${rec.fpl_id}`} className="font-display font-bold text-sm hover:text-fa-mint">{rec.name}</Link>
                                            <div className="font-mono text-[9px] text-white/45">{rec.team} · {rec.position} · £{rec.price}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${badge.cls}`}>{badge.label}</span>
                                        <span className="font-mono text-[10px] text-white/55 tabular-nums">fit {rec.fit_score}</span>
                                    </div>
                                </div>
                                {rec.strengths && <p className="font-mono text-[10px] text-white/55 mt-1.5 pl-1">{rec.strengths}</p>}
                                {rec.reasons?.length > 0 && (
                                    <div className="mt-1.5 space-y-0.5 border-t border-white/5 pt-1.5">
                                        {rec.reasons.map((rr, i) => (
                                            <div key={i} className="font-mono text-[10px] text-white/60">
                                                <span className="text-fa-sky">{rr.user}:</span> {rr.text}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Odeslání tipu */}
            {user ? (
                data.my_submissions >= data.my_limit ? (
                    <p className="font-mono text-[10px] text-fa-yellow" data-testid="scout-limit-msg">Vyčerpal jsi {data.my_limit} doporučení na tento seznam (palce + vlastní tipy).</p>
                ) : (
                    <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="scout-submit-box">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">
                            Doplň vlastního hráče ({data.my_submissions}/{data.my_limit} doporučení) · +1 Knowledge XP
                        </div>
                        <PlayerPicker onPick={setPicked} />
                        {picked && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-display font-bold text-sm text-fa-mint" data-testid="scout-picked">{picked.name} · £{picked.price}</span>
                                <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
                                       placeholder="Proč? (nepovinné)" data-testid="scout-reason"
                                       className="flex-1 min-w-[200px] bg-black border border-white/15 font-mono text-[11px] px-3 py-1.5 text-white" />
                                <button type="button" onClick={submit} data-testid="scout-submit-btn"
                                        className="font-mono text-[10px] uppercase tracking-widest bg-fa-mint text-black font-bold px-3 py-2 flex items-center gap-1">
                                    <PaperPlaneTilt size={11} weight="bold" /> Odeslat tip
                                </button>
                            </div>
                        )}
                    </div>
                )
            ) : (
                <p className="font-mono text-[10px] text-white/45">Pro posílání tipů se přihlas.</p>
            )}

            {/* Doplněné tipy komunity */}
            <div className="space-y-2">
                <h3 className="font-display font-black uppercase text-sm">Doplněné tipy komunity ({data.players.length})</h3>
                {data.players.length === 0 && <p className="font-mono text-[10px] text-white/40">Zatím nikdo nic nedoplnil — buď první.</p>}
                {data.players.map((p) => (
                    <div key={p.fpl_id} className="border border-white/10 bg-[#0d0d0d] p-3" data-testid={`scout-tip-${p.fpl_id}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-3">
                                <span className="font-display font-black text-lg text-fa-mint tabular-nums w-8">{p.count}×</span>
                                <div>
                                    <Link to={`/players/${p.fpl_id}`} className="font-display font-bold text-sm hover:text-fa-mint">{p.name}</Link>
                                    <div className="font-mono text-[9px] text-white/45">{p.team} · {p.position} · £{p.price}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {p.reasons.length > 0 && (
                                    <button type="button" onClick={() => setOpenReasons((o) => ({ ...o, [p.fpl_id]: !o[p.fpl_id] }))}
                                            data-testid={`scout-reasons-toggle-${p.fpl_id}`}
                                            className="font-mono text-[9px] text-white/50 hover:text-white flex items-center gap-1">
                                        {p.reasons.length} důvodů {openReasons[p.fpl_id] ? <CaretUp size={10} /> : <CaretDown size={10} />}
                                    </button>
                                )}
                                {p.my_tip && (
                                    <button type="button" onClick={() => retract(p.fpl_id)} data-testid={`scout-retract-${p.fpl_id}`}
                                            className="font-mono text-[9px] text-red-300 hover:text-red-200 flex items-center gap-1">
                                        <Trash size={10} /> můj tip
                                    </button>
                                )}
                            </div>
                        </div>
                        {openReasons[p.fpl_id] && (
                            <div className="mt-2 space-y-1 border-t border-white/5 pt-2">
                                {p.reasons.map((rr, i) => (
                                    <div key={i} className="font-mono text-[10px] text-white/60">
                                        <span className="text-fa-sky">{rr.user}:</span> {rr.text}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function ScoutHub() {
    const { user } = useAuth();
    const [focuses, setFocuses] = useState(null);
    const [board, setBoard] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        scoutFocuses().then((d) => setFocuses(d.items || [])).catch(() => setFocuses([]));
        scoutLeaderboard().then(setBoard).catch(() => {});
    }, []);

    return (
        <AppShell>
            <div className="space-y-6" data-testid="scout-hub-page">
                <header className="border-2 border-fa-sky/40 bg-gradient-to-br from-fa-sky/10 via-transparent to-fa-mint/5 p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-sky flex items-center gap-2">
                        <Binoculars size={11} weight="fill" /> Komunita · Scouting
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Scout Hub</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Vystav svůj Recruitment Focus komunitě, nebo scoutuj pro ostatní — za každý tip +1 Knowledge XP. Z tipů skládáme komunitní žebříčky.
                    </p>
                </header>

                {selected ? (
                    <FocusDetail fid={selected} onBack={() => setSelected(null)} user={user} />
                ) : (
                    <>
                        <section className="space-y-2">
                            <h2 className="font-display font-black uppercase text-base">Veřejné focusy</h2>
                            {focuses === null && <p className="font-mono text-[11px] text-white/40">Načítám…</p>}
                            {focuses?.length === 0 && (
                                <p className="font-mono text-[11px] text-white/45">
                                    Zatím žádný veřejný focus. Vystav svůj v nástroji <Link to="/tools/recruitment-focus" className="text-fa-mint underline">Recruitment Focus</Link>.
                                </p>
                            )}
                            <div className="grid md:grid-cols-2 gap-3">
                                {(focuses || []).map((f) => (
                                    <button key={f.id} type="button" onClick={() => setSelected(f.id)}
                                            data-testid={`scout-focus-${f.id}`}
                                            className="border border-white/10 bg-[#0d0d0d] p-4 text-left hover:border-fa-sky/50 transition-colors">
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-fa-sky">
                                            {TYPE_LABEL[f.type] || f.type} · {f.owner_name}{f.is_mine ? " (ty)" : ""}
                                        </div>
                                        <div className="font-display font-black text-base mt-0.5">{f.title}</div>
                                        <div className="font-mono text-[9px] text-white/45 mt-1 flex gap-2 flex-wrap">
                                            {f.position_filters?.length > 0 && <span>{f.position_filters.join("/")}</span>}
                                            {f.budget_max != null && <span>do £{f.budget_max}</span>}
                                            <span>GW {f.gw_start}–{f.gw_end}</span>
                                            <span className="text-fa-mint">{f.submissions} tipů</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>

                        {/* Žebříčky */}
                        <section className="grid md:grid-cols-2 gap-4">
                            <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="scout-leaderboard-scouts">
                                <h3 className="font-display font-black uppercase text-sm flex items-center gap-1.5 mb-2">
                                    <Trophy size={14} className="text-fa-yellow" /> Top scouti
                                </h3>
                                {(board?.top_scouts || []).length === 0 && <p className="font-mono text-[10px] text-white/40">Zatím prázdné.</p>}
                                {(board?.top_scouts || []).map((s, i) => (
                                    <div key={s.user_id} className="flex items-center justify-between py-1 border-b border-white/5 font-mono text-[11px]">
                                        <span><span className="text-white/40">{i + 1}.</span> <span className="font-bold">{s.name}</span></span>
                                        <span className="text-white/55">{s.tips} tipů · {s.focuses} focusů</span>
                                    </div>
                                ))}
                            </div>
                            <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="scout-leaderboard-players">
                                <h3 className="font-display font-black uppercase text-sm flex items-center gap-1.5 mb-2">
                                    <Binoculars size={14} className="text-fa-mint" /> Nejžádanější hráči
                                </h3>
                                {(board?.most_wanted || []).length === 0 && <p className="font-mono text-[10px] text-white/40">Zatím prázdné.</p>}
                                {(board?.most_wanted || []).map((p, i) => (
                                    <div key={p.fpl_id} className="flex items-center justify-between py-1 border-b border-white/5 font-mono text-[11px]">
                                        <span>
                                            <span className="text-white/40">{i + 1}.</span>{" "}
                                            <Link to={`/players/${p.fpl_id}`} className="font-bold hover:text-fa-mint">{p.name}</Link>
                                            <span className="text-white/40"> · {p.team}</span>
                                        </span>
                                        <span className="text-white/55">{p.tips} tipů</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </>
                )}
            </div>
        </AppShell>
    );
}
